import { fileUrl } from "@/lib/api";
import { barcodeBars, waveBackgroundPath, textAnchorX, textLines, cfgFor } from "./liveRender";
import { BG_CATEGORY_MAP, resolveFontStack } from "./designerUtils";

function MultilineText({ x, y, width, text, fontFamily, fontSize, bold, align, color }) {
  const { anchor, tx } = textAnchorX(x, width, align);
  const lines = textLines(text);
  return (
    <>
      {lines.map((line, i) => (
        <text
          key={i}
          x={tx}
          y={y + fontSize * 0.95 + i * fontSize * 1.3}
          fontFamily={fontFamily || "sans-serif"}
          fontSize={fontSize}
          fontWeight={bold ? "bold" : "normal"}
          textAnchor={anchor}
          fill={color}
        >
          {line}
        </text>
      ))}
    </>
  );
}

function Barcode({ upc, x, y, w, h, color = "#111111" }) {
  const { bars, barH, textH, grouped } = barcodeBars(upc, w, h);
  const pad = h * 0.5;
  return (
    <g>
      <rect x={x - pad} y={y} width={w + 2 * pad} height={h} fill="#ffffff" />
      {bars.map((b, i) => (
        <rect key={i} x={x + b.x} y={y} width={b.width} height={barH} fill={color} />
      ))}
      <text x={x + w / 2} y={y + barH + textH * 0.85} fontFamily="monospace" fontSize={textH * 0.7} textAnchor="middle" fill={color} letterSpacing="2">
        {grouped}
      </text>
    </g>
  );
}

function ModeASvg({ product, template, brand, customContent }) {
  const custom = customContent || {};
  const W = Number(template.widthMM),
    H = Number(template.heightMM);
  const ec = template.elementConfig || {};
  const bgField = BG_CATEGORY_MAP[product?.category] || "default";
  const backgrounds = brand?.backgrounds || {};
  const bgValue = custom.backgroundOverride || backgrounds[bgField] || backgrounds.default || "#F5F0E8";
  const colors = brand?.colors || {};
  const primary = colors.primary || "#1E293B";
  const secondary = colors.secondary || "#B59A78";
  const textColor = colors.text || "#1E293B";
  const fonts = brand?.fonts || {};
  const isColorBg = typeof bgValue === "string" && bgValue.startsWith("#");

  const fontSizes = custom.fontSize || {};
  const fontFamilies = custom.fontFamily || {};
  const bolds = custom.bold || {};
  const aligns = custom.align || {};
  const textColors = custom.textColor || {};

  const logoKey = custom.logoKey || "main";
  const logos = brand?.logos || {};
  const logoUrl = custom.logoUrlOverride || logos[logoKey] || logos.main;
  const logoCfg = cfgFor(ec, "logo", custom);

  const imgCfg = cfgFor(ec, "productImage", custom);
  const prodImgUrl = custom.productImageOverride || product?.mainImage?.url;

  const materialCfg = cfgFor(ec, "material", custom);
  const piecesCfg = cfgFor(ec, "pieces", custom);
  const upcCfg = cfgFor(ec, "upc", custom);

  const textField = (key, productKey, defaultSize, defaultFont, defaultBold = false) => {
    const c = cfgFor(ec, key, custom);
    if (!c) return null;
    const value = custom[key] ?? product?.[productKey] ?? "";
    const size = fontSizes[key] ?? defaultSize;
    const bold = bolds[key] ?? defaultBold;
    const align = aligns[key] || "left";
    const color = textColors[key] || textColor;
    const font = fontFamilies[key] ? resolveFontStack(fontFamilies[key]) : defaultFont;
    return <MultilineText key={key} x={c.x} y={c.y} width={c.width} text={value} fontFamily={font} fontSize={size} bold={bold} align={align} color={color} />;
  };

  const piecesVal = custom.pieces ?? product?.pieces ?? "";
  const piecesText = piecesVal !== null && piecesVal !== undefined && piecesVal !== "" ? `${piecesVal} PIECES` : "";
  const materialText = String(custom.material ?? product?.material ?? "").toUpperCase();

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto", display: "block" }} data-testid="live-svg-canvas">
      <rect x="0" y="0" width={W} height={H} fill="#ffffff" />
      {isColorBg ? (
        <>
          <rect x="0" y="0" width={W} height={H} fill={bgValue} />
          <path d={waveBackgroundPath(W, H)} fill={primary} opacity="0.92" />
        </>
      ) : (
        <image href={fileUrl(bgValue)} x="0" y="0" width={W} height={H} preserveAspectRatio="xMidYMid slice" />
      )}

      {logoCfg && logoUrl && <image href={fileUrl(logoUrl)} x={logoCfg.x} y={logoCfg.y} width={logoCfg.width} height={logoCfg.height} preserveAspectRatio="xMidYMid meet" />}

      {imgCfg && prodImgUrl && (
        <>
          <clipPath id="pimgclip">
            <rect x={imgCfg.x} y={imgCfg.y} width={imgCfg.width} height={imgCfg.height} rx="2" />
          </clipPath>
          <image href={fileUrl(prodImgUrl)} x={imgCfg.x} y={imgCfg.y} width={imgCfg.width} height={imgCfg.height} preserveAspectRatio="xMidYMid slice" clipPath="url(#pimgclip)" />
        </>
      )}

      {textField("productTitle", "productName", 13, fonts.titleFont || "sans-serif", true)}
      {textField("size", "size", 8, fonts.bodyFont || "sans-serif")}

      {materialCfg && (
        <>
          <rect x={materialCfg.x} y={materialCfg.y} width={materialCfg.width} height={materialCfg.height} rx="2" fill={secondary} />
          <MultilineText x={materialCfg.x} y={materialCfg.y} width={materialCfg.width} text={materialText} fontFamily={fontFamilies.material ? resolveFontStack(fontFamilies.material) : (fonts.bodyFont || "sans-serif")} fontSize={fontSizes.material ?? 6} bold={bolds.material ?? true} align="center" color="#FFFFFF" />
        </>
      )}

      {piecesCfg && (
        <MultilineText x={piecesCfg.x} y={piecesCfg.y} width={piecesCfg.width} text={piecesText} fontFamily={fontFamilies.pieces ? resolveFontStack(fontFamilies.pieces) : (fonts.bodyFont || "sans-serif")} fontSize={fontSizes.pieces ?? 6} bold={bolds.pieces ?? false} align={aligns.pieces || "left"} color={textColors.pieces || textColor} />
      )}

      {textField("description", "description", 6, fonts.bodyFont || "sans-serif")}

      {upcCfg && <Barcode upc={custom.customUPC ?? product?.upc ?? ""} x={upcCfg.x} y={upcCfg.y} w={upcCfg.width} h={upcCfg.height} color={textColor} />}

      {(custom.addedTexts || []).map((item, i) => (
        <MultilineText key={`at-${i}`} x={item.x ?? 10} y={item.y ?? 10} width={item.width ?? 80} text={item.text || ""} fontFamily={item.font || "sans-serif"} fontSize={item.fontSize ?? 8} bold={item.bold} align={item.align || "left"} color={item.color || textColor} />
      ))}

      {(custom.addedImages || []).map(
        (item, i) =>
          item.url && (
            <image key={`ai-${i}`} href={fileUrl(item.url)} x={item.x ?? 10} y={item.y ?? 10} width={item.width ?? 60} height={item.height ?? 60} preserveAspectRatio="xMidYMid meet" />
          )
      )}
    </svg>
  );
}

function ModeBSvg({ customContent }) {
  const custom = customContent || {};
  const w = custom.canvasWidth || 1600;
  const h = custom.canvasHeight || 1131;
  const bgUrl = custom.backgroundImage;
  const bgColor = custom.backgroundColorOverride;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} style={{ width: "100%", height: "auto", display: "block" }} data-testid="live-svg-canvas">
      {bgColor ? <rect x="0" y="0" width={w} height={h} fill={bgColor} /> : bgUrl && <image href={fileUrl(bgUrl)} x="0" y="0" width={w} height={h} />}
      {(custom.overlays || []).map((ov, i) => {
        const x = ov.x ?? 0,
          y = ov.y ?? 0,
          width = ov.width ?? 100,
          height = ov.height ?? 50;
        const wrapTransform = (el) => {
          if (!ov.transform) return el;
          const [a, b, c, d] = ov.transform;
          const cx = x + width / 2, cy = y + height / 2;
          return (
            <g key={i} transform={`translate(${cx},${cy}) matrix(${a},${b},${c},${d},0,0) translate(${-cx},${-cy})`}>
              {el}
            </g>
          );
        };
        if (ov.type === "text") {
          return wrapTransform(<MultilineText key={i} x={x} y={y} width={width} text={ov.text || ""} fontFamily={ov.font || "sans-serif"} fontSize={ov.fontSize ?? 24} bold={ov.bold} align={ov.align || "left"} color={ov.color || "#111111"} />);
        }
        if (ov.type === "upc") {
          return wrapTransform(<Barcode key={i} upc={ov.text || "000000000000"} x={x} y={y} w={width} h={height} color={ov.color || "#111111"} />);
        }
        if (ov.type === "image" || ov.type === "logo") {
          return ov.url ? wrapTransform(<image key={i} href={fileUrl(ov.url)} x={x} y={y} width={width} height={height} preserveAspectRatio="xMidYMid meet" />) : null;
        }
        if (ov.type === "cover") {
          return <rect key={i} x={x} y={y} width={width} height={height} fill={ov.color || "#ffffff"} />;
        }
        return null;
      })}
    </svg>
  );
}

export default function LiveSvgPreview({ mode, product, template, brand, customContent }) {
  if (mode === "A") {
    if (!template) return null;
    return <ModeASvg product={product} template={template} brand={brand} customContent={customContent} />;
  }
  return <ModeBSvg customContent={customContent} />;
}
